/**
 * Password Login Form for Lender App
 * Refactored to use shared components and Tailwind
 */

import { yupResolver } from "@hookform/resolvers/yup";
import { Eye, EyeOff } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@krea/shared/design-system/Card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@krea/shared/design-system/Form";
import { Input } from "@krea/shared/design-system/inputs/Input";
import { useAuthStore } from "@krea/shared/features/auth/authStore";

import { useLoginMutation } from "@/features/auth/hooks/data/use-login.mutation";

const loginValidationSchema = Yup.object().shape({
  ssn: Yup.string().required("SSN is required"),
  password: Yup.string().required("Password is required"),
});

type LoginFormData = Yup.InferType<typeof loginValidationSchema>;

interface PasswordLoginFormProps {
  onSwitchToBankID?: () => void;
}

export const PasswordLoginForm: React.FC<PasswordLoginFormProps> = ({
  onSwitchToBankID,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(false);

  const { from } = (location.state as {
    from: { pathname: string };
  } | null) ?? {
    from: { pathname: "/" },
  };
  from.pathname = from.pathname === "/login" ? "/" : from.pathname;

  const form = useForm<LoginFormData>({
    resolver: yupResolver(loginValidationSchema),
    defaultValues: {
      ssn: "",
      password: "",
    },
  });
  const { mutate: loginMutation } = useLoginMutation();

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    reset,
  } = form;
  const { setAccessToken, setRefreshToken } = useAuthStore();

  const onSubmit = async (data: LoginFormData) => {
    loginMutation(data, {
      onSuccess: (response) => {
        setLoginError(false);
        reset();
        setAccessToken(response.accessToken);
        setRefreshToken(response.refreshToken);
        navigate(from);
      },
      onError: () => {
        setLoginError(true);
      },
    });
  };

  return (
    <div className="mx-auto w-full max-w-xl">
      <Card className="p-8">
        <CardHeader className="px-0">
          <CardTitle className="text-center text-2xl text-gray-800">
            Log in
          </CardTitle>
        </CardHeader>

        <CardContent className="px-0">
          {/* Error Message */}
          <AnimatePresence initial={true}>
            {loginError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-6"
              >
                <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                  Invalid SSN or password
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Form */}
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* SSN Field */}
              <FormField
                control={control}
                name="ssn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personal number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="YYYYMMDDXXXX"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          field.onChange(e);
                          setLoginError(false);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Field */}
              <FormField
                control={control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={isShowPassword ? "text" : "password"}
                          disabled={isSubmitting}
                          onChange={(e) => {
                            field.onChange(e);
                            setLoginError(false);
                          }}
                          className="pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setIsShowPassword(!isShowPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-700"
                          tabIndex={-1}
                        >
                          {isShowPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting || !isValid}
                  className="w-full py-3"
                  size="lg"
                >
                  {isSubmitting ? "Logging in..." : "Log in"}
                </Button>
              </div>

              {/* Forgot Password Link */}
              <div className="text-center">
                <Button
                  variant="link"
                  onClick={() => {
                    window.location.href =
                      "mailto:kundservice@krea.se?subject=Lender%20user%20forgot%20password";
                  }}
                >
                  Forgot Password?
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Back to BankID */}
      {onSwitchToBankID && (
        <div className="mt-6 text-center">
          <Button onClick={onSwitchToBankID} variant="link">
            Login with BankID instead
          </Button>
        </div>
      )}
    </div>
  );
};
